import React, { useCallback, useEffect, useState } from "react";
import classNames from "./Billing.module.css";
import api from "../../../hooks/api-new";
import { useAxios } from "../../../hooks/axios";
import useUser from "../../../hooks/user";
import {
  useCurrentPlan,
  useOrganization,
} from "../../context/organization/organization";
import Loader from "../../common/Loader";
import { NeedHelp } from "./Support";
import {
  IoCheckmark,
  IoChevronDown,
  IoChevronUp,
  IoClose,
} from "react-icons/io5";
import Button from "../../common/Button";
import Select from "../../common/Select";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

function Features(props: {
  features: { enabled: boolean; name: string }[];
  landing?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      onClick={() => setOpen((prev) => !prev)}
      className={classNames.IncludedFeatures}
    >
      <Button
        borderColor="transparent"
        backgroundColor="transparent"
        style={{
          color: !props.landing ? "white" : "black",
          padding: 0,
          alignSelf: "flex-start",
        }}
      >
        Included features
        {!open ? <IoChevronDown /> : <IoChevronUp />}
      </Button>
      {open && (
        <div>
          {props.features.map((feature, index) => (
            <div key={index} className={classNames.Feature}>
              {feature.enabled ? (
                <IoCheckmark />
              ) : (
                <IoClose style={{ color: "#cc525f" }} />
              )}
              {feature.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface PricingTier {
  name: string;
  tag?: string;
  tagText?: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonAction: () => void;
}

export const PricingTiers = (props: { currentPlan?: string }) => {
  const [addHours, setAddHours] = useState(20);
  const axios = useAxios();
  const createCheckout = api.useCreateStripeCheckout(axios);
  const [user] = useUser();

  const clickGetADemo = useCallback(() => {
    window.open("https://calendly.com/snow-huo-byrdhouse/30min", "_blank");
  }, []);
  const navigate = useNavigate();

  const pricingTiers: PricingTier[] = [
    {
      name: "starter",
      tag: "20 minutes free trial",
      description:
        "For organizations with occasional multilingual meetings and events",
      features: [
        "Flexible, pay-as-you-go",
        "Single language pair",
        "Supports multiple participants per session",
      ],
      buttonText: props.currentPlan ? "Get credits" : "Try for free",
      buttonAction: props.currentPlan
        ? async () => {
            if (!user.auth) return;
            const response = await createCheckout({
              quantity: addHours,
            });
            if (response.data.url) window.open(response.data.url);
          }
        : () => navigate("/login"),
    },
    {
      name: "pro",
      description:
        "For organizations scaling multilingual meetings, webinars, and events",
      features: [
        "Minimum 20 hours annual commitment",
        "Up to 50 languages per meeting or event",
        "Subscription based with volume discount",
      ],
      buttonText: "Get a Demo",
      buttonAction: clickGetADemo,
    },
    {
      name: "business",
      description:
        "For organizations with frequent multilingual meetings, webinars, events, and conferences",
      features: [
        "Minimum 100 hours annual commitment",
        "Unlimited participants",
        "Platform integration available",
      ],
      buttonText: "Contact Sales",
      buttonAction: clickGetADemo,
    },
  ];

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.has("success")) {
      toast(
        <div className="Toast SuccessToast">
          {"Successfully purchased trasnlation hours"}
        </div>,
        {
          type: "success",
        }
      );
    }
  }, [searchParams]);

  return (
    <div className={classNames.TiersContainer}>
      {pricingTiers.map((tier, index) => (
        <div
          key={index}
          className={`${classNames.PricingTier} ${
            props.currentPlan === tier.name ? classNames.CurrentTier : ""
          }`}
        >
          {/* <div className={classNames.TierHeader}>
              {tier.tag && (
                <span className={classNames.TrialTag}>{tier.tag}</span>
                )}
            </div> */}
          <h3>{tier.description}</h3>
          <h2>
            {tier.name.charAt(0).toUpperCase() +
              tier.name.slice(1).toLowerCase()}
          </h2>
          <div className={classNames.FirstFeatures}>
            {tier.features.map((feature, index) => (
              <div key={index} className={classNames.Feature}>
                <IoCheckmark />
                {feature}
              </div>
            ))}
          </div>
          <div className={classNames.Buttons}>
            {props.currentPlan && tier.name === "starter" && (
              <Select
                selected={addHours}
                onChange={(value) => setAddHours(value as number)}
                items={Array.from({ length: 20 }, (_, i) => {
                  const num = i + 1;
                  return {
                    value: num,
                    label: num + " hour" + (num === 1 ? "" : "s"),
                  };
                })}
                borderColor="gray"
              />
            )}
            <Button
              style={{
                flexGrow: "1",
              }}
              onClick={tier.buttonAction}
              backgroundColor={!props.currentPlan ? "blue" : undefined}
              borderColor={!props.currentPlan ? "blue" : undefined}
            >
              {tier.buttonText}
            </Button>
          </div>
          <Features
            features={[
              { name: "Real-time voice-to-text translation", enabled: true },
              { name: "Real-time voice-to-voice translation", enabled: true },
              { name: "24/7 on-demand access", enabled: true },
              { name: "Global accent and dialect coverage", enabled: true },
              { name: "Smart profanity filtering", enabled: true },
              { name: "Custom glossaries and terminology", enabled: true },
              {
                name: "Flexible for in-person, virtual and hybrid meetings and events",
                enabled: true,
              },
              { name: "Live multilingual Q&A", enabled: true },
              { name: "Cross-device compatibility", enabled: true },
              { name: "Speaker recognition and labeling", enabled: true },
              { name: "Multi-speaker translation", enabled: true },
              {
                name: "Human-like AI voice avatars",
                enabled: tier.name !== "starter",
              },
              {
                name: "Automatic language detection",
                enabled: tier.name !== "starter",
              },
              {
                name: "Dedicated channel for human interpreters",
                enabled: tier.name !== "starter",
              },
              { name: "Customer support ", enabled: tier.name !== "starter" },
              {
                name: "Multilingual meeting recordings",
                enabled: tier.name !== "starter" && tier.name !== "pro",
              },
              {
                name: "Multilingual meeting notes, summaries and highlights",
                enabled: tier.name !== "starter" && tier.name !== "pro",
              },
              {
                name: "Industry-specific customization and personalization",
                enabled: tier.name !== "starter" && tier.name !== "pro",
              },
              {
                name: "Seamless platform or telephony integrations",
                enabled: tier.name !== "starter" && tier.name !== "pro",
              },
              {
                name: "Technical support and guided onboarding",
                enabled: tier.name !== "starter" && tier.name !== "pro",
              },
            ]}
            landing={!props.currentPlan}
          />
        </div>
      ))}
    </div>
  );
};

export function Pricing() {
  const { organization, loading } = useOrganization();

  const currentPlan = useCurrentPlan(organization);

  if (!organization || loading) {
    return <Loader />;
  }

  return (
    <section className={classNames.PricingSection}>
      <h2>Pricing & plan</h2>

      <PricingTiers currentPlan={currentPlan} />
    </section>
  );
}

const Billing: React.FC = () => {
  return (
    <div className={classNames.Billing}>
      <h1>Plan & billing</h1>

      <Pricing />

      <NeedHelp title="Need help with billing?" />
    </div>
  );
};

export default Billing;
